import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandLessRounded from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import ArrowDownwardRounded from "@mui/icons-material/ArrowDownwardRounded";
import LoopRounded from "@mui/icons-material/LoopRounded";
import { useNavigate } from "react-router-dom";

const TYPE_LABELS = {
  inference: "Inference",
  script: "Script",
  sequence: "Sequence",
  parallel: "Parallel",
  loop: "Loop",
  stream: "Stream",
  retrieval: "Retrieval",
  conditional: "Conditional",
};

const TYPE_COLORS = {
  inference: "primary",
  script: "default",
  sequence: "secondary",
  parallel: "warning",
  loop: "info",
  stream: "secondary",
  retrieval: "info",
  conditional: "warning",
};

const Connector = () => (
  <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
    <ArrowDownwardRounded fontSize="small" color="action" />
  </Box>
);

const PromptGraphDiagram = ({
  graph = [],
  tciId,
  pendingByPromptId = {},
  onNodePanelOpen,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    graph.forEach((node) => {
      if (["sequence", "parallel", "loop"].includes(node.nodeType)) {
        initial[node.nodeId] = true;
      }
    });
    return initial;
  });

  const toggleExpanded = (nodeId) => {
    setExpanded((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const renderInferenceActions = (node) => {
    const pending = pendingByPromptId[node.promptId] || [];
    const pendingCount = pending.length;

    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
        {pendingCount > 0 && (
          <Chip
            size="small"
            color="error"
            label={`${pendingCount} pending`}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/dashboard/tci-manager/candidates/${pending[0].id}`);
            }}
            sx={{ cursor: "pointer" }}
          />
        )}
        <Button
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            navigate(
              `/dashboard/tci-manager/tcis/${encodeURIComponent(tciId)}/prompts/${encodeURIComponent(node.promptId)}`,
            );
          }}
        >
          Open QueryManager →
        </Button>
      </Stack>
    );
  };

  const renderNodeCard = (node, { nested = false } = {}) => {
    const nodeType = node.nodeType;
    const isContainer = ["sequence", "parallel", "loop"].includes(nodeType);
    const isPanelType = ["script", "stream", "retrieval", "conditional"].includes(nodeType);
    const isOpen = expanded[node.nodeId] !== false;

    const primaryId =
      nodeType === "inference"
        ? node.promptId
        : nodeType === "script"
          ? node.queryId || "Deterministic"
          : nodeType === "stream"
            ? node.transport
            : nodeType === "retrieval"
              ? node.retrievalType
              : null;

    return (
      <Paper
        key={node.nodeId}
        variant="outlined"
        sx={{
          p: 1.5,
          ml: nested ? 2 : 0,
          borderStyle: nested ? "dashed" : "solid",
          cursor: isPanelType ? "pointer" : "default",
        }}
        onClick={
          isPanelType
            ? () => onNodePanelOpen?.(node)
            : undefined
        }
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2">{node.label}</Typography>
            {primaryId && (
              <Typography variant="caption" sx={{ fontFamily: "monospace" }} color="text.secondary">
                {primaryId}
              </Typography>
            )}
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                label={TYPE_LABELS[nodeType] || nodeType}
                color={TYPE_COLORS[nodeType] || "default"}
              />
              {nodeType === "parallel" && node.mergeStrategy && (
                <Chip size="small" label={node.mergeStrategy} variant="outlined" />
              )}
              {nodeType === "loop" && (
                <Chip size="small" label={`max ${node.maxIterations ?? 20}`} variant="outlined" />
              )}
            </Stack>
            {nodeType === "inference" && renderInferenceActions(node)}
            {nodeType === "loop" && node.exitCondition && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontStyle: "italic" }}>
                {node.exitCondition}
              </Typography>
            )}
          </Box>
          {isContainer && (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                toggleExpanded(node.nodeId);
              }}
              aria-label={isOpen ? "Collapse" : "Expand"}
            >
              {isOpen ? <ExpandLessRounded /> : <ExpandMoreRounded />}
            </IconButton>
          )}
        </Box>

        {nodeType === "sequence" && (
          <Collapse in={isOpen}>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {(node.children || []).map((child) => renderNodeCard(child, { nested: true }))}
            </Stack>
          </Collapse>
        )}

        {nodeType === "parallel" && (
          <Collapse in={isOpen}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1}
              sx={{ mt: 1 }}
            >
              {(node.children || []).map((child) => (
                <Box key={child.nodeId} sx={{ flex: 1 }}>
                  {renderNodeCard(child, { nested: true })}
                </Box>
              ))}
            </Stack>
          </Collapse>
        )}

        {nodeType === "loop" && (
          <Collapse in={isOpen}>
            <Box sx={{ mt: 1, position: "relative" }}>
              {node.child && renderNodeCard(node.child, { nested: true })}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1, ml: 2 }}>
                <LoopRounded fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  Loop back until exit condition
                </Typography>
              </Box>
            </Box>
          </Collapse>
        )}
      </Paper>
    );
  };

  const sorted = [...graph].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <Stack spacing={0}>
      {sorted.map((node, index) => (
        <React.Fragment key={node.nodeId}>
          {renderNodeCard(node)}
          {index < sorted.length - 1 ? <Connector /> : null}
        </React.Fragment>
      ))}
    </Stack>
  );
};

export default PromptGraphDiagram;
